/**
 * Collapsible "Edit dealer" form. Self-gates on `dealer.write`.
 *
 * Prefilled from the existing row. Edits name / stock limit / risk tier;
 * suspension state is governed by the suspend / reinstate actions, not here.
 * Omitted fields are left unchanged (PATCH semantics).
 *
 * On success the cached detail row is replaced and the list query invalidated.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { Dealer, DealerRiskTier, UpdateDealerRequest } from "@dfp/api-client";
import { Button } from "./Button.js";
import { Field, FormError, Select, TextInput, apiErrorMessage } from "./Form.js";
import { useAuth } from "../auth/AuthContext.js";
import { api } from "../lib/api.js";

const RISK_TIERS: readonly DealerRiskTier[] = ["A", "B", "C"];

export function EditDealerForm({ dealer }: { readonly dealer: Dealer }): JSX.Element | null {
  const auth = useAuth();
  const canWrite = (auth.user?.permissions ?? []).includes("dealer.write");
  const qc = useQueryClient();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState(dealer.name);
  const [stockLimit, setStockLimit] = useState(String(dealer.stockLimit));
  const [riskTier, setRiskTier] = useState<DealerRiskTier>(dealer.riskTier);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (body: UpdateDealerRequest) => api.patch<Dealer>(`/api/dealers/${dealer.id}`, body),
    onSuccess: (next) => {
      qc.setQueryData<Dealer>(["dealers", "byId", dealer.id], next);
      void qc.invalidateQueries({ queryKey: ["dealers", "list"] });
      setError(null);
      setOpen(false);
    },
    onError: (err: unknown) => setError(apiErrorMessage(err)),
  });

  function reset(): void {
    setName(dealer.name);
    setStockLimit(String(dealer.stockLimit));
    setRiskTier(dealer.riskTier);
    setError(null);
  }

  function submit(): void {
    setError(null);
    if (name.trim().length < 1) {
      setError("Name is required.");
      return;
    }
    const stock = Number(stockLimit);
    if (!Number.isInteger(stock) || stock < 0) {
      setError("Stock limit must be a non-negative whole number.");
      return;
    }
    mutation.mutate({ name: name.trim(), stockLimit: stock, riskTier });
  }

  if (!canWrite) return null;

  if (!open) {
    return (
      <Button variant="ghost" onClick={() => setOpen(true)}>
        Edit dealer…
      </Button>
    );
  }

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3">
      <p className="text-sm font-medium text-slate-700">Edit dealer</p>

      <Field label="Name">
        <TextInput value={name} onChange={(e) => setName(e.target.value)} maxLength={200} />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Stock limit" hint="whole number">
          <TextInput
            inputMode="numeric"
            value={stockLimit}
            onChange={(e) => setStockLimit(e.target.value)}
          />
        </Field>
        <Field label="Risk tier">
          <Select value={riskTier} onChange={(e) => setRiskTier(e.target.value as DealerRiskTier)}>
            {RISK_TIERS.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <FormError message={error} />

      <div className="flex gap-2">
        <Button onClick={submit} disabled={mutation.isPending || name.trim().length < 1}>
          {mutation.isPending ? "Saving…" : "Save changes"}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            reset();
            setOpen(false);
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
